/**
 * Mengenrabatt-Waehler auf der Produktseite.
 *
 * Haengt sich an das Mengenfeld des Kaufformulars, statt ein eigenes zu
 * fuehren. Beide Richtungen sind verdrahtet: eine Stufenkarte setzt die Menge,
 * und wer Plus, Minus oder die Tastatur benutzt, bewegt den Balken mit. Ein
 * zweiter, eigener Mengenzustand waere die naheliegende Loesung und genau die
 * falsche, weil dann zwei Zahlen auf der Seite stuenden, die auseinanderlaufen
 * koennen, und nur eine davon in den Warenkorb geht.
 *
 * Die Prozente kommen aus dem Produkt-Metafeld hess.volume_tiers, aus dem auch
 * die Shopify Function rechnet. Die Betraege werden hier aus dem Variantenpreis
 * berechnet und nirgends gespeichert. Wer sie stattdessen im Metafeld ablegen
 * wollte, haette zwei Wahrheiten ueber denselben Preis.
 *
 * Der Rabatt selbst wird NICHT hier vergeben. Diese Datei zeigt an, was die
 * Function im Warenkorb abzieht. Weichen beide voneinander ab, ist das ein
 * Fehler in dieser Datei, nicht im Preis.
 */
class VolumeTiers extends HTMLElement {
  connectedCallback() {
    this.config = this.readConfig();
    if (!this.config) return;

    this.tiers = (this.config.tiers || [])
      .map((tier) => ({
        minQuantity: Number(tier.min_quantity),
        percentage: Number(tier.percentage),
      }))
      .filter((tier) => tier.minQuantity > 0 && tier.percentage > 0)
      .sort((a, b) => a.minQuantity - b.minQuantity);

    if (!this.tiers.length) return;

    this.cards = Array.from(this.querySelectorAll("[data-volume-card]"));
    this.quantityOutput = this.querySelector("[data-volume-quantity]");
    this.savingOutputs = Array.from(this.querySelectorAll("[data-volume-saving]"));
    this.scaleMarks = Array.from(this.querySelectorAll("[data-volume-scale]"));
    this.fill = this.querySelector("[data-volume-fill]");
    this.steps = Array.from(this.querySelectorAll("[data-volume-step]"));

    this.track = this.querySelector(".volume-tiers__track");
    // Reihenfolge zaehlt: findQuantityInputs braucht die Variante, um Felder
    // ausserhalb der eigenen Section diesem Produkt zuordnen zu koennen.
    this.variantInput = this.findVariantInput();
    this.quantityInputs = this.findQuantityInputs();

    // Die Marken haengen an den Kartenmitten, und die verschieben sich mit
    // der Fensterbreite. Ohne diesen Beobachter stimmen sie nach einer
    // Drehung des Geraets oder einem Umbruch der Karten nicht mehr.
    if (typeof ResizeObserver === "function") {
      this.observer = new ResizeObserver(() => this.update());
      this.observer.observe(this);
    }

    this.cards.forEach((card) => {
      card.addEventListener("click", () => this.selectQuantity(Number(card.dataset.quantity)));
    });

    this.bound = new WeakSet();
    this.bindQuantityInputs();

    // Der Variantenwechsel rendert Teile der Section neu und tauscht dabei
    // das sichtbare Mengenfeld gegen ein FRISCHES aus, das wieder bei 1 steht.
    // Ohne diesen Beobachter haelt das Element danach einen Knoten, der nicht
    // mehr im Dokument haengt: die Kopfzeile meldet weiter "Menge: 4",
    // waehrend vor dem Kunden 1 steht und 1 in den Warenkorb ginge.
    //
    // Ein MutationObserver und nicht der Variantenwechsel allein, weil der
    // Austausch asynchron nach dem Ereignis passiert und auch andere Ursachen
    // haben kann. Das Setzen von input.value aendert nur eine Eigenschaft und
    // keinen Knoten, loest hier also keine Schleife aus.
    // Ab Menge 2 fragt der Set-Konfigurator, wie sich die Menge auf die
    // Stimmungen verteilt. Abgefangen wird submit auf DOKUMENT-Ebene in der
    // Erfassungsphase, nicht am Formular: product-form.js haengt selbst an
    // submit des Formulars, und Zuhoerer am selben Ziel laufen in der
    // Reihenfolge ihrer Registrierung. Nur weiter oben im Baum ist sicher,
    // dass wir zuerst drankommen und stopPropagation greift.
    this.onSubmitCapture = this.onSubmitCapture.bind(this);
    document.addEventListener("submit", this.onSubmitCapture, true);

    const section = this.closest(".shopify-section");
    if (section && typeof MutationObserver === "function") {
      this.domObserver = new MutationObserver(() => this.rebindQuantityInputs());
      this.domObserver.observe(section, { childList: true, subtree: true });
    }

    if (this.variantInput) {
      // Der Variantenwaehler feuert dieses Event bereits selbst, siehe
      // variants-picker.js, updateVariantInput. Kein eigenes Polling und kein
      // Section-Rerender noetig.
      this.variantInput.addEventListener("change", () => this.update());
    }

    this.currentQuantity = this.quantity;
    this.update();
  }

  readConfig() {
    const node = this.querySelector("[data-volume-tiers-config]");
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      // Kaputte Konfiguration heisst: gar nichts anzeigen. Ein Waehler mit
      // halben Daten wuerde falsche Betraege versprechen.
      console.error("[volume-tiers] Konfiguration nicht lesbar", error);
      return null;
    }
  }

  /**
   * ALLE Mengenfelder der Section, nicht nur eines.
   *
   * Die Produktseite fuehrt drei Stueck: eines mit form="<id>" am Kaufformular,
   * ein sichtbares ohne form-Attribut, und eines in der Sticky-Leiste. Die
   * ersten beiden tragen sogar dieselbe id, was ungueltiges HTML ist, aber so
   * im Theme steht.
   *
   * Bis zum 31.08.2026 suchte diese Funktion ueber die Formular-ID und traf
   * damit das unsichtbare. Der Waehler meldete "Menge: 4", das Feld unter ihm
   * zeigte weiter 1, und in den Warenkorb ging 1. Der Rabatt fiel damit aus,
   * waehrend die Seite ihn ankuendigte, also genau der Fall, den dieses
   * Bauteil vermeiden soll.
   *
   * Deshalb: alle einsammeln, alle gleich halten. Wer eines aendert, aendert
   * ueber onQuantityInput die anderen mit, samt change-Event, damit die
   * Zuhoerer des Themes ebenfalls reagieren.
   */
  findQuantityInputs() {
    const scope = this.closest(".shopify-section");
    const own = scope ? Array.from(scope.querySelectorAll('input[name="quantity"]')) : [];

    // Die Sticky-Kaufleiste ist eine eigene Section mit eigenem Formular und
    // eigenem Mengenfeld. Sie gehoert trotzdem zu diesem Produkt, und das
    // Theme haelt sie mit nichts synchron: QUANTITY_CHANGE wird in global.js
    // nur gesendet, niemand hoert darauf. Wer ueber die Leiste kauft, bekaeme
    // sonst 1 Stueck, waehrend die Seite einen Mengenrabatt anzeigt.
    //
    // Zugeordnet wird ueber die Variante, nicht ueber eine Section-ID: ein
    // Feld gehoert dazu, wenn sein Formular dieselbe Varianten-ID fuehrt.
    // Eine hartcodierte ID wuerde brechen, sobald jemand die Leiste umbenennt
    // oder eine zweite hinzukommt. Empfehlungen und Schnellkauf-Formulare
    // anderer Produkte tragen andere IDs und fallen dadurch heraus.
    const variantId = this.variantInput ? this.variantInput.value : null;
    const foreign = variantId
      ? Array.from(document.querySelectorAll('input[name="quantity"]')).filter((input) => {
          if (own.indexOf(input) !== -1) return false;
          // input.form beruecksichtigt auch das form-Attribut, nicht nur den
          // umschliessenden Baum.
          // belongsToProduct prueft dasselbe wie fuer die Formulare:
          // [name="id"] deckt input und select ab. Die Sticky-Leiste fuehrt
          // ihre Variante als select und fiel durch einen Filter, der nur
          // input kannte.
          return this.belongsToProduct(input.form);
        })
      : [];

    const all = own.concat(foreign);
    return all.length ? all : Array.from(document.querySelectorAll('input[name="quantity"]'));
  }

  /**
   * Gelesen wird aus dem Feld, das der Kunde vor sich hat. Ein unsichtbares
   * kann veraltet sein, das sichtbare ist die Wahrheit.
   */
  get primaryInput() {
    return (
      this.quantityInputs.find((input) => input.offsetWidth || input.offsetHeight) ||
      this.quantityInputs[0] ||
      null
    );
  }

  onQuantityInput(source) {
    this.currentQuantity = this.readQuantity(source.value);
    this.propagate(source.value, source);
    this.update();
  }

  readQuantity(value) {
    const raw = parseInt(value, 10);
    return Number.isFinite(raw) && raw > 0 ? raw : 1;
  }

  /**
   * Setzt den Wert in allen uebrigen Feldern und meldet das jeweils per
   * change weiter. Die Sperre verhindert, dass die dadurch ausgeloesten
   * Handler erneut verteilen.
   */
  propagate(value, source) {
    if (this.syncing) return;
    this.syncing = true;
    this.quantityInputs.forEach((input) => {
      if (input === source || input.value === value) return;
      input.value = value;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
    this.syncing = false;
  }

  /**
   * [name="id"] und nicht input[name="id"]: das Hauptformular fuehrt die
   * Variante als verstecktes input, die Sticky-Leiste als select. Hier zaehlt
   * nur das Hauptformular, aber die enge Fassung war schon einmal die
   * Ursache eines Fehlers und kostet nichts.
   */
  findVariantInput() {
    const formId = this.dataset.formId;
    if (formId) {
      const form = document.getElementById(formId);
      const byForm = form && form.querySelector('[name="id"]');
      if (byForm) return byForm;
    }
    const scope = this.closest(".shopify-section") || document;
    return scope.querySelector('[name="id"]');
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.domObserver) this.domObserver.disconnect();
    document.removeEventListener("submit", this.onSubmitCapture, true);
  }

  /**
   * Gehoert dieses Formular zu unserem Produkt? Gemessen an der Variante,
   * nicht an einer Section-ID, aus demselben Grund wie bei den Mengenfeldern.
   * [name="id"] deckt input und select ab.
   */
  belongsToProduct(form) {
    if (!form || !this.variantInput) return false;
    const id = form.querySelector('[name="id"]');
    return Boolean(id) && id.value === this.variantInput.value;
  }

  /**
   * Der Eingriff in den Kaufbutton ist der heikelste Teil dieses Bauteils:
   * geht er schief, kann niemand mehr kaufen. Deshalb greift er nur, wenn
   * ALLE Bedingungen erfuellt sind, und laesst den Kauf sonst unberuehrt
   * durchlaufen. Fehlender oder kaputter Konfigurator heisst normaler Kauf,
   * nicht blockierter Kauf.
   */
  onSubmitCapture(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!this.belongsToProduct(form)) return;
    if (this.currentQuantity < 2) return;

    const builder = document.querySelector("volume-set-builder");
    if (!builder || !builder.isReady) return;

    event.preventDefault();
    event.stopPropagation();
    builder.prepare(this.currentQuantity, this.variantInput.value);
    builder.open(event.submitter || null);
  }

  /**
   * Haengt die Zuhoerer an alle noch nicht verdrahteten Mengenfelder.
   *
   * change deckt Plus, Minus und das Verlassen des Feldes ab, input deckt das
   * Tippen ab. Ohne input bliebe der Balken beim Eintippen von "12" erst auf 1
   * und spraenge dann.
   */
  bindQuantityInputs() {
    this.quantityInputs.forEach((input) => {
      if (this.bound.has(input)) return;
      this.bound.add(input);
      input.addEventListener("change", () => this.onQuantityInput(input));
      input.addEventListener("input", () => this.onQuantityInput(input));
    });
  }

  /**
   * Nach einem Neuaufbau der Section: Felder neu einsammeln und die zuletzt
   * gewaehlte Menge wiederherstellen. Die Menge kommt aus this.currentQuantity
   * und nicht aus den Feldern, weil ein frisch eingesetztes Feld wieder 1
   * traegt und die Wahl des Kunden sonst still verloren ginge.
   */
  rebindQuantityInputs() {
    const fresh = this.findQuantityInputs();
    const unveraendert =
      fresh.length === this.quantityInputs.length &&
      fresh.every((input, i) => input === this.quantityInputs[i]);
    if (unveraendert) return;

    this.quantityInputs = fresh;
    this.bindQuantityInputs();
    this.propagate(String(this.currentQuantity), null);
    this.update();
  }

  /**
   * Ankerpunkte des Balkens: die Mitte jeder Stufenkarte, als Prozent der
   * Balkenbreite.
   *
   * Der Balken laeuft im Kartenraster, nicht auf der Mengenskala. Das ist
   * keine Feinheit, sondern der Grund, warum Marke und Betrag uebereinander
   * stehen: die Betraege in der Kopfzeile sitzen mittig ueber ihrer Karte.
   * Rechnete der Balken stattdessen Stufe geteilt durch Hoechststufe, laege
   * die letzte Marke auf 100 Prozent, ihr Betrag aber ueber der Kartenmitte
   * bei rund 83 Prozent. Gemessen waren das 67 px Abstand, und das Dreieck
   * zeigte neben die Zahl, auf die es zeigt.
   *
   * Gemessen statt gerechnet, weil die Rasterabstaende sonst mit einflossen
   * und die Naeherung mit steigender Stufenzahl schlechter wuerde.
   */
  measureAnchors() {
    if (!this.track) return [];
    const track = this.track.getBoundingClientRect();
    if (!track.width) return [];

    return this.cards
      .map((card) => {
        const box = card.getBoundingClientRect();
        return {
          quantity: Number(card.dataset.quantity),
          percent: ((box.left + box.width / 2 - track.left) / track.width) * 100,
        };
      })
      .filter((anchor) => Number.isFinite(anchor.quantity) && Number.isFinite(anchor.percent))
      .sort((a, b) => a.quantity - b.quantity);
  }

  /**
   * Fuellstand zur Menge.
   *
   * Der Balken wandert innerhalb einer Stufe mit: von 4 auf 5 ruckt er ein
   * Stueck vor, und so weiter bis zur 10. Bis zum 31.08.2026 stand er auf
   * halber Strecke und ruehrte sich zwischen zwei Schwellen nicht, was die
   * Frage "wie weit fehlt mir noch" unbeantwortet liess.
   *
   * Der Anfang jeder Stufe liegt eine halbe Markenbreite HINTER ihrer Marke.
   * Dadurch liegt die gerade erreichte Marke vollstaendig auf dem Gruen und
   * schwebt nicht mit ihrer rechten Haelfte ueber dem grauen Teil. Beim
   * Ueberschreiten einer Schwelle springt der Balken deshalb um diese halbe
   * Breite vor, ein kleiner Ruck genau im richtigen Moment.
   *
   * Hoechste Stufe erreicht: voll. Es ist nichts mehr zu holen, und ein
   * Balken, der bei 90 Prozent stehenbliebe, behauptete das Gegenteil.
   */
  progressFor(quantity, anchors) {
    const first = anchors[0];
    const last = anchors[anchors.length - 1];
    if (quantity <= first.quantity) return first.percent;
    if (quantity >= last.quantity) return 100;

    const versatz = this.overshootPercent();
    for (let i = 0; i < anchors.length - 1; i += 1) {
      const from = anchors[i];
      const to = anchors[i + 1];
      if (quantity < from.quantity || quantity >= to.quantity) continue;

      // Der erste Abschnitt liegt vor jeder Stufe, dort gibt es keine Marke,
      // die im Gruen liegen muesste, also auch keinen Versatz.
      const start = i === 0 ? from.percent : from.percent + versatz;
      const spanne = to.quantity - from.quantity;
      const anteil = spanne > 0 ? (quantity - from.quantity) / spanne : 0;
      return start + (to.percent - start) * anteil;
    }
    return 100;
  }

  /**
   * Halbe Markenbreite, in Prozent der Balkenbreite. Gemessen und nicht
   * gerechnet, weil beides von der Schriftbasis des Themes abhaengt.
   */
  overshootPercent() {
    const step = this.steps && this.steps[0];
    if (!step || !this.track) return 0;
    const bahn = this.track.getBoundingClientRect();
    if (!bahn.width) return 0;
    return (step.getBoundingClientRect().width / 2 / bahn.width) * 100;
  }

  get quantity() {
    const input = this.primaryInput;
    return this.readQuantity(input ? input.value : 1);
  }

  get unitPrice() {
    const prices = this.config.variantPrices || {};
    const id = this.variantInput && this.variantInput.value;
    const price = id ? prices[id] : undefined;
    if (typeof price === "number") return price;
    // Kein Treffer: den ersten hinterlegten Preis nehmen, statt 0 zu rechnen.
    // 0 waere "du sparst 0,00 Euro" und damit eine Falschaussage, die wie eine
    // Aussage aussieht.
    const first = Object.values(prices)[0];
    return typeof first === "number" ? first : 0;
  }

  tierFor(quantity) {
    let match = null;
    this.tiers.forEach((tier) => {
      if (quantity >= tier.minQuantity) match = tier;
    });
    return match;
  }

  selectQuantity(quantity) {
    if (!Number.isFinite(quantity) || quantity < 1) return;
    // Bubbelndes change auf jedem Feld, damit die uebrigen Zuhoerer des Themes
    // (unter anderem QuantityInput in global.js, das daraufhin sein eigenes
    // FoxThemeEvents-Signal sendet) genauso reagieren wie auf einen Klick auf
    // Plus. Ein stilles Setzen von value feuert nichts.
    this.currentQuantity = quantity;
    this.propagate(String(quantity), null);
    this.update();
  }

  update() {
    const quantity = this.quantity;
    const unitPrice = this.unitPrice;
    const tier = this.tierFor(quantity);
    const texts = this.config.texts || {};

    if (this.quantityOutput) this.quantityOutput.textContent = String(quantity);

    // Die gruene Ersparnis steht in der Spalte der geltenden Stufe, nicht
    // neben der Menge. Genau eine Spalte zeigt sie, alle anderen bleiben leer:
    // eine ueberholte Stufe hat nichts mehr zu sagen, und eine noch nicht
    // erreichte traegt ihren grauen Betrag.
    //
    // Der Betrag rechnet mit der EINGESTELLTEN Menge, nicht mit der Schwelle.
    // Bei Menge 5 und einer Stufe ab 4 sind das 24,50 € und nicht 19,60 €,
    // sonst stuende dort weniger, als der Warenkorb tatsaechlich abzieht.
    const saving = tier ? Math.round((quantity * unitPrice * tier.percentage) / 100) : 0;
    this.savingOutputs.forEach((output) => {
      const active = Boolean(tier) && Number(output.dataset.quantity) === tier.minQuantity && saving > 0;
      output.hidden = !active;
      // Nur die Zahl, nicht der Satz. Der Wortteil steht im Markup und ist
      // damit uebersetzbar; wuerde er hier ueberschrieben, stuende auf der
      // englischen Fassung nach dem ersten Klick wieder Deutsch.
      const betrag = output.querySelector("[data-volume-amount]");
      if (active && betrag) betrag.textContent = this.money(saving);
    });

    // Die Skala nennt je Schwelle den Betrag, den genau diese Schwelle bringt,
    // und zeigt nur noch die Schwellen, die NOCH NICHT erreicht sind. Was
    // erreicht ist, steht als "Du sparst" in der Kopfzeile; beides zugleich
    // waere dieselbe Zahl zweimal.
    //
    // Die Betraege haengen am Variantenpreis, nicht an der Menge, und muessen
    // deshalb auch beim Variantenwechsel neu geschrieben werden. Das Schreiben
    // passiert vor dem Ausblenden, damit eine Marke, die spaeter wieder
    // auftaucht, keinen Preis von der alten Variante zeigt.
    this.scaleMarks.forEach((mark) => {
      const markQuantity = Number(mark.dataset.quantity);
      const markPercentage = Number(mark.dataset.percentage);
      const markSaving = Math.round((markQuantity * unitPrice * markPercentage) / 100);

      const amount = mark.querySelector("[data-volume-amount]");
      if (amount) amount.textContent = this.money(markSaving);

      // Der Vorlesetext nennt nur noch die Menge und bleibt dadurch fest im
      // Markup stehen. Frueher trug er auch den Betrag und musste hier
      // neu geschrieben werden, was ihn unuebersetzbar machte. Den Betrag
      // liest der Screenreader jetzt aus der sichtbaren Zeile daneben.
      mark.hidden = quantity >= markQuantity;
    });

    const anchors = this.measureAnchors();

    // Ohne messbare Anker (Element ausgeblendet, Breite 0) bleibt die
    // Naeherung aus dem Liquid stehen, statt alles auf 0 zu setzen.
    if (anchors.length) {
      this.steps.forEach((step) => {
        const anchor = anchors.find((a) => a.quantity === Number(step.dataset.quantity));
        if (anchor) step.style.left = `${anchor.percent}%`;
      });

      if (this.fill) this.fill.style.width = `${this.progressFor(quantity, anchors)}%`;
    }

    this.steps.forEach((step) => {
      step.classList.toggle("is-reached", quantity >= Number(step.dataset.quantity));
    });

    this.cards.forEach((card) => {
      const cardQuantity = Number(card.dataset.quantity);
      const active = tier ? cardQuantity === tier.minQuantity : cardQuantity === 1;
      card.classList.toggle("is-selected", active);
      card.setAttribute("aria-pressed", active ? "true" : "false");
    });
  }

  fillText(template, values) {
    let text = template || "";
    Object.keys(values).forEach((key) => {
      text = text.split(`{${key}}`).join(values[key]);
    });
    return text;
  }

  money(cents) {
    const format = window.FoxThemeSettings && window.FoxThemeSettings.money_format;
    if (typeof window.formatMoney === "function") {
      return window.formatMoney(cents, format);
    }
    // Nur als Notnagel, wenn global.js nicht geladen ist. Deutsches Format,
    // weil der Shop in Euro verkauft.
    return `${(cents / 100).toFixed(2).replace(".", ",")} €`;
  }
}

if (!customElements.get("volume-tiers")) {
  customElements.define("volume-tiers", VolumeTiers);
}
